import CloseIcon from "@mui/icons-material/Close";
import {
  Typography,
  Dialog,
  Paper,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";

import { Product } from "api/memberPortal/project/product/product.types";
import { PRODUCT_STATUSES } from "api/memberPortal/project/product/productStatuses.type";
import { useAppForm } from "components/Forms/AppForm";
import TestedFeaturesSection from "components/Forms/components/sections/TestedFeaturesSection/TestedFeaturesSection";
import { updateProductProgressPoint } from "components/UI/Functions/progressFunctions";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";

import { useProductFormData } from "./hooks/useProductFormData";
import { useProductMutations } from "./hooks/useProductMutations";
import AddEvalTeamSection from "./ProductFormSections/AddEvalTeamSection";
import CAVPSection from "./ProductFormSections/CAVPSection";
import EarSection from "./ProductFormSections/EarSection/EarSection";
import FileUploadSection from "./ProductFormSections/FileUploadSection";
import IsResubmissionSection from "./ProductFormSections/IsResubmissionSection";
import PPSection from "./ProductFormSections/PPSection";
import ProductDetailsSection from "./ProductFormSections/ProductDetailsSection";
import ProgressPointDateInputSection from "./ProductFormSections/ProgressPointDateInputSection";
import PublishSelectSection from "./ProductFormSections/PublishSelectSection";
import RejectionRationaleSection from "./ProductFormSections/RejectionRationalSection";
import VendorInfoSection from "./ProductFormSections/VendorInfoSection";
import {
  ProductFormSchema,
  type ProductFormValues,
} from "./Schemas/ProductForm.schema";

interface ProductFormModalProps {
  show: boolean;
  onClose: () => void;
  onShowAlertAndRefetch: (_message: string, _type: string) => void;
  product_id?: number;
  switchForms: (_product: Product) => void;
}

const ProductFormModal = ({
  show,
  onClose,
  onShowAlertAndRefetch,
  product_id,
  switchForms,
}: ProductFormModalProps) => {
  const [error, setError] = useState<string>("");
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();
  const { authToken, csrfToken } = useAuth();
  const {
    product,
    productFiles,
    productPps,
    earData,
    csComponents,
    evaluators,
    labVendors,
    productFeatures,
    isLoading,
  } = useProductFormData(product_id);

  const originalEarIds = earData.map((ear) => ear.earid);
  const originalFeatureIds = productFeatures.map((feature) => feature.id);

  const {
    triggerCreateProduct,
    triggerUpdateProduct,
    handleRemoveEar,
    handleEarUpload,
    handleFileUpload,
    handleFeatureChanges,
  } = useProductMutations();

  const productFormDefaultValues = {
    csfc: {
      csfc_components:
        csComponents?.map((csfc) => ({
          value: csfc.component,
          label: csfc.component,
        })) ?? [],
      csfc_eligible: product?.csfc_eligible ?? "",
    },
    submissionType: {
      is_resubmission: product?.is_resubmission ?? false,
      previous_vid:
        product?.previous_vid?.map((v) => ({
          value: v.previous_product?.v_id,
          label: "VID" + v.previous_product?.v_id,
        })) ?? [],
    },

    ears: {
      earData:
        earData.length > 0
          ? earData.map((ear) => ({
              earid: ear.earid,
              apriori: ear.apriori,
              earFiles: ear.files.map((file) => ({
                file_id: file.file_id,
                fileType: file.file_display_name,
                fileLabel: file.file_label,
                fileName: file.file_name,
                active: file.active,
              })),
            }))
          : [],
      ear_required: product?.ear_required ?? false,
      no_ear_justification: product?.no_ear_justification ?? "",
    },
    evaluator_checkout_date: product?.evaluator_checkout_date
      ? moment.utc(product.evaluator_checkout_date).format("YYYY-MM-DD")
      : "",
    files: productFiles.map((file) => ({
      file_id: file.file_id, // is this something we need?
      file: undefined,
      fileType: file?.file_display_name ?? "",
      documentVisibility: file.private ? "Proprietary" : "Public",
      fileLabel: file?.file_label ?? "",
      fileName: file?.file_name ?? "",
      active: file.active ?? true,
    })),
    formState: "draft-create",
    nist: {
      nist_completion: product?.nist_completion
        ? moment.utc(product.nist_completion).format("YYYY-MM-DD")
        : "",
      nist_required: product?.nist_required ?? false,
    },

    product_description: product?.product_description ?? "",
    product_id: product.product_id,
    product_name: product?.product_name ?? "",
    protectionProfiles: {
      sbom_submitted: product?.sbom_submitted ?? false,
      protection_profile:
        productPps?.map((pp) => ({
          value: pp.pp__pp_id ?? "",
          label: pp.pp__pp_short_name ?? pp.pp__pp_name ?? "",
        })) ?? [],
    },
    publish_status: product?.publish_status ?? "",
    status: product?.status ?? PRODUCT_STATUSES.DRAFT,
    st_evaluation_date: product?.st_evaluation_date
      ? moment.utc(product.st_evaluation_date).format("YYYY-MM-DD")
      : "",
    switchForm: false,
    tech_type:
      product?.tech_type?.split(",").map((tech) => ({
        value: tech,
        label: tech,
      })) ?? [],
    test_ready_date: product?.test_ready_date
      ? moment.utc(product.test_ready_date).format("YYYY-MM-DD")
      : "",
    testing_complete_date: product?.testing_complete_date
      ? moment.utc(product.testing_complete_date).format("YYYY-MM-DD")
      : "",
    testedFeatures:
      productFeatures?.map((pf) => ({
        id: pf.id,
        feature: pf.feature,
        feature_name: pf.feature_name,
      })) ?? [],
    url: product?.url ?? "",
    vendor_email: product?.vendor_email ?? "",
    vendor_id: (product?.vendor_id as { org_id: number })?.org_id ?? 0,
    vendor_phone: product?.vendor_phone ?? "",
    vendor_poc: product?.vendor_poc ?? "",
    version: product?.version ?? "",
  } as ProductFormValues;

  const productForm = useAppForm({
    defaultValues: productFormDefaultValues,

    validators: {
      onSubmit: ProductFormSchema,
    },

    onSubmit: async ({ value }) => {
      setError(""); // Clear any stale error from a previous failed submission

      // we check if there is a lead evaluator assigned
      const hasLeadEvaluator = evaluators.some(
        (member) => member.role === "Lead Evaluator",
      );

      if (
        !hasLeadEvaluator &&
        !value.switchForm &&
        value.status !== PRODUCT_STATUSES.DRAFT
      ) {
        setError("Please Assign a Lead Evaluator to Evaluation Team.");
        hideLoadingSpinner();

        return;
      }

      const handlePostSuccess = (data: Product, value: ProductFormValues) => {
        handleFileUpload("product", data.product_id, value.files);
        handleEarUpload(value, data.product_id);
        handleFeatureChanges(
          originalFeatureIds,
          value.testedFeatures,
          data.product_id,
        );
        switchFormOrClose(data, value);
      };

      try {
        const { formState } = value;
        const isUpdate =
          product.product_id &&
          (formState === "draft-update" || formState === "final");

        if (isUpdate) {
          showLoadingSpinner("Updating Product...");

          value.ears.earData &&
            handleRemoveEar(originalEarIds, value.ears.earData);

          const data = await triggerUpdateProduct({
            ...value,
            product_id: product.product_id,
          }).unwrap();

          handlePostSuccess(data, value);
        } else {
          showLoadingSpinner("Creating Product...");
          const data = await triggerCreateProduct(value).unwrap();

          handlePostSuccess(data, value);
        }
      } catch (err) {
        hideLoadingSpinner();
        setError(
          (err as { data?: { url?: string }; status?: number })?.data?.url ??
            "Error saving Check-In Form. Please try again.",
        );
      }
    },

    canSubmitWhenInvalid: true,
    onSubmitInvalid: async () => {
      setError("Please fill out all required fields before submitting");
    },
  });

  useEffect(() => {
    if (isLoading) {
      showLoadingSpinner("Loading Check-In Form...");
    } else {
      hideLoadingSpinner();
    }
  }, [isLoading, showLoadingSpinner, hideLoadingSpinner]);

  if (isLoading) return null;

  const switchFormOrClose = (data: Product, value: ProductFormValues) => {
    if (value.switchForm) {
      showLoadingSpinner("Please wait while we switch forms...");
      switchForms(data);
    } else {
      productForm.setFieldValue("switchForm", false);
      if (data.status === PRODUCT_STATUSES.DRAFT) {
        const message = `Product VID${data.v_id} has been saved and exists as a Draft.`;
        onShowAlertAndRefetch(message, "success");
      } else if (data.status === PRODUCT_STATUSES.PRE_PROPOSAL) {
        updateProductProgressPoint(data.product_id, 1, "In Progress", {
          authToken,
          csrfToken,
        });
        const message = `Product VID${data.v_id} has been submitted to Vendor for Signing.`;
        onShowAlertAndRefetch(message, "success");
      } else {
        const message = `Product VID${data.v_id} has been updated.`;
        onShowAlertAndRefetch(message, "success");
      }
      hideLoadingSpinner();
      onClose();
    }
  };

  return (
    <Dialog
      open={show}
      fullScreen
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
        hideLoadingSpinner();
      }}
      sx={{
        padding: "1em",
        margin: 0,
      }}
      slotProps={{
        paper: {
          sx: {
            margin: "64px",
            "&.MuiDialog-paperScrollBody": {
              maxWidth: "900px",
            },
          },
        },
      }}
      maxWidth="md"
      scroll="body"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogTitle>
          <Box textAlign="center" mt={2}>
            {/* WARN: Custom styling from pre standardization! */}
            {/* TODO: Standardize when you come across this!  */}
            <Typography variant="h1" fontSize={20} fontWeight="bold" mb={2}>
              NIAP Check-In Form
            </Typography>
            {/* WARN: Custom styling from pre standardization! */}
            {/* TODO: Standardize when you come across this!  */}
            <Typography variant="h2" fontSize={16} fontWeight="bold" mb={1}>
              Product Evaluations Claiming Exact Compliance with a NIAP Approved
              PP {product?.product_id ? "VID " + product.v_id : ""}
            </Typography>
            {/* WARN: Custom styling from pre standardization! */}
            {/* TODO: Standardize when you come across this!  */}
            <Typography variant="body1">
              PLEASE FILL OUT ALL INFORMATION BELOW
            </Typography>

            {/* WARN: Custom styling from pre standardization! */}
            {/* TODO: Standardize when you come across this!  */}
            {product.v_id && (
              <Typography variant="h2" fontSize={20} fontWeight="bold" mb={1}>
                EDITING: VID{product.v_id}
              </Typography>
            )}
          </Box>

          {/* TODO: MOVE INTO FORM COMPONENTS */}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider
          variant="fullWidth"
          sx={{ opacity: ".25", borderColor: "black" }}
        />

        <DialogContent>
          <RejectionRationaleSection product={product} />

          <IsResubmissionSection productForm={productForm} />

          <ProductDetailsSection productForm={productForm} />

          <PPSection productForm={productForm} productPps={productPps} />

          <PublishSelectSection productForm={productForm} />

          <FileUploadSection productForm={productForm} />

          <ProgressPointDateInputSection productForm={productForm} />

          <EarSection productForm={productForm} />

          <VendorInfoSection
            productForm={productForm}
            labVendors={labVendors}
          />

          <AddEvalTeamSection
            productForm={productForm}
            product_id={product.product_id}
          />

          <TestedFeaturesSection
            form={productForm}
            fieldName="testedFeatures"
          />

          <CAVPSection productForm={productForm} />
        </DialogContent>
        <Paper elevation={1} sx={{ zIndex: 100 }}>
          <DialogActions sx={{ padding: 1.5, flexDirection: "column", gap: 2 }}>
            {error && (
              <Alert color="error" variant="standard" sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                variant="contained"
                color="error"
                sx={{ width: 150 }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                sx={{
                  minWidth: 150,
                }}
                onClick={() => {
                  productForm.reset({
                    ...productForm.state.values,
                    switchForm: false,
                    status: PRODUCT_STATUSES.DRAFT,
                    formState: `draft-${product.product_id ? "update" : "create"}`,
                  });
                  productForm.handleSubmit();
                }}
              >
                {product.product_id ? "Update Draft" : "Save as Draft"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  minWidth: 150,
                }}
                onClick={() => {
                  productForm.reset({
                    ...productForm.state.values,
                    status: PRODUCT_STATUSES.PRE_PROPOSAL,
                    formState: "final",
                  });
                  productForm.handleSubmit();
                }}
              >
                Submit to Vendor for Signing
              </Button>
            </Box>
          </DialogActions>
        </Paper>
      </form>
    </Dialog>
  );
};

export default ProductFormModal;
